import { useCallback, useMemo } from "react";
import type { PageType } from "./use-app-state";

interface NavigationState {
  currentPage: PageType;
  navigationHistory: PageType[];
  setCurrentPage: (page: PageType) => void;
  setNavigationHistory: (history: PageType[]) => void;
  navigatedFromMenu: boolean;
  setNavigatedFromMenu: (value: boolean) => void;
  setIsMenuOpen: (value: boolean) => void;
}

/**
 * Custom hook for managing navigation with history tracking
 * Optimized to prevent unnecessary state updates and re-renders
 */
export function useNavigation(state: NavigationState) {
  // Navigate to a page and add current page to history
  const navigateToPage = useCallback((page: PageType) => {
    if (state.currentPage === page) {
      // Don't navigate if we're already on this page
      return;
    }

    // Add current page to history before navigating
    state.setNavigationHistory([...state.navigationHistory, state.currentPage]);
    state.setCurrentPage(page);
  }, [state]);

  const restoreMenuIfNeeded = useCallback((shouldRestoreMenu: boolean) => {
    if (!shouldRestoreMenu) return;
    // Use setTimeout to ensure the navigation completes first.
    setTimeout(() => {
      state.setIsMenuOpen(true);
      state.setNavigatedFromMenu(false);
    }, 0);
  }, [state]);

  const isValidHistoryPage = useCallback((p: PageType | undefined): p is PageType => {
    if (!p) return false;
    // Never "back" into login; fallback should keep users inside app safe area.
    return p !== "login";
  }, []);

  // Safe back navigation with explicit fallback.
  const safeBack = useCallback((fallbackPage: PageType = "home") => {
    // Check if we should restore the menu state
    const shouldRestoreMenu = state.navigatedFromMenu;

    if (state.navigationHistory.length > 0) {
      const history = [...state.navigationHistory];
      // Skip invalid pages in restored history.
      while (history.length > 0) {
        const previousPage = history.pop();
        if (!isValidHistoryPage(previousPage)) continue;
        state.setNavigationHistory(history);
        state.setCurrentPage(previousPage);
        restoreMenuIfNeeded(shouldRestoreMenu);
        return;
      }
    }
    state.setNavigationHistory([]);
    state.setCurrentPage(fallbackPage);
    restoreMenuIfNeeded(shouldRestoreMenu);
  }, [state, isValidHistoryPage, restoreMenuIfNeeded]);

  // Navigate back using history
  const navigateBack = useCallback(() => {
    safeBack("home");
  }, [safeBack]);

  // Navigate to home and clear history
  const navigateToHome = useCallback((clearSearch: boolean = false) => {
    state.setCurrentPage("home");
    state.setNavigationHistory([]);
    
    return { clearSearch };
  }, [state.setCurrentPage, state.setNavigationHistory]);

  // Check if we can go back
  const canGoBack = useMemo(() => {
    return state.navigationHistory.length > 0;
  }, [state.navigationHistory.length]);

  return {
    navigateToPage,
    navigateBack,
    safeBack,
    navigateToHome,
    canGoBack,
  };
}